import { createAsyncThunk } from '@reduxjs/toolkit';
import { isRecord } from '@skynet/type-guards';
import { disconnect, send } from './redux-websocket';
import type { WsConnectionStatus, WsConnectionState } from './types';

export interface WsConnectThunksConfig {
  /**
   * Key of the WS connection state slice in the Redux store.
   * The store is accessed as `state[sliceKey]` to read the connection status.
   */
  sliceKey: string;
  /** The WS channel prefix (e.g. WsPrefixes.mmPrefix). */
  prefix: string;
  /** Command to send on logout before disconnecting. */
  logoutCommand?: string;
  /** Command to send on explicit disconnect. */
  disconnectCommand?: string;
}

/**
 * How long `wsLogout`/`wsDisconnect` wait after sending their command before
 * closing the socket from the client side, so the server can process it.
 */
export const DISCONNECT_GRACE_MS = 100;

function isWsConnectionState(value: unknown): value is WsConnectionState {
  return (
    isRecord(value) &&
    'status' in value &&
    typeof value.status === 'string' &&
    'isReconnecting' in value
  );
}

function getWsStatus(state: unknown, sliceKey: string): WsConnectionStatus {
  if (isRecord(state)) {
    const slice = state[sliceKey];
    if (isWsConnectionState(slice)) {
      return slice.status;
    }
  }
  return 'idle';
}

/**
 * Creates typed async thunks for WS connect/logout/disconnect/subscribe flows.
 *
 * The returned `subscribeWsData` thunk accepts a pre-built connect action as its
 * argument, so the URL-reading logic (which needs env vars) can stay in the consumer.
 *
 * @example
 * // In data-ws-mm:
 * const { wsLogout, wsDisconnect, subscribeWsData } = createWsConnectThunks({
 *   sliceKey: 'wsMm',
 *   prefix: WsPrefixes.mmPrefix,
 *   logoutCommand: 'Logout',
 *   disconnectCommand: 'Disconnect',
 * });
 *
 * // In the dashboard (connecting):
 * dispatch(subscribeWsData(wsMmConnect()));
 */
export const createWsConnectThunks = (config: WsConnectThunksConfig) => {
  const { sliceKey, prefix, logoutCommand, disconnectCommand } = config;

  /**
   * Creates a thunk that sends `command` (if any), waits
   * {@link DISCONNECT_GRACE_MS} for the server to act on it, then closes the
   * socket from the client side — unless it is no longer connected by then.
   * Servers close the socket themselves on Logout and Disconnect, and on a
   * low-latency link that close lands within the wait; disconnecting an
   * already-closed socket emits no close event, which would leave the slice
   * stuck at `disconnecting` and block every later connect.
   */
  const createCloseThunk = (name: string, command: string | undefined) =>
    createAsyncThunk<void, void, { state: unknown }>(`${prefix}/${name}`, async (_, thunkAPI) => {
      if (getWsStatus(thunkAPI.getState(), sliceKey) !== 'connected') {
        return;
      }
      if (command) {
        thunkAPI.dispatch(send({ command }, prefix));
      }
      await new Promise<void>((resolve) => setTimeout(resolve, DISCONNECT_GRACE_MS));
      // Checked again: the server may have closed the socket during the wait
      if (getWsStatus(thunkAPI.getState(), sliceKey) === 'connected') {
        thunkAPI.dispatch(disconnect(prefix));
      }
    });

  const wsLogout = createCloseThunk('logout', logoutCommand);
  const wsDisconnect = createCloseThunk('disconnect', disconnectCommand);

  /** Dispatches the given connect action only when the channel is idle. */
  const subscribeWsData = createAsyncThunk<
    void,
    ReturnType<typeof import('./redux-websocket/actions').connect>,
    { state: unknown }
  >(`${prefix}/subscribe`, (connectAction, thunkAPI) => {
    const wsStatus = getWsStatus(thunkAPI.getState(), sliceKey);
    if (wsStatus === 'idle') {
      thunkAPI.dispatch(connectAction);
    }
  });

  return { wsLogout, wsDisconnect, subscribeWsData };
};
